import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";

export const PayPalCheckout = ({ eventId, disabled, label = "or pay with PayPal" }) => {
  const [config, setConfig] = useState({ loading: true, enabled: false, client_id: "", mode: "sandbox" });
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/payments/paypal/config")
      .then(({ data }) => setConfig({ loading: false, ...data }))
      .catch((err) => {
        console.error("[PayPal] config fetch failed", err);
        setConfig({ loading: false, enabled: false, client_id: "", mode: "sandbox" });
      });
  }, []);

  if (config.loading) {
    return (
      <div data-testid="paypal-loading" className="h-12 rounded-xl bg-[#131A26] border border-white/10 flex items-center justify-center text-slate-500 text-sm">
        <Loader2 size={14} className="animate-spin mr-2" /> Loading PayPal...
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div
        data-testid="paypal-disabled"
        className="h-12 rounded-xl bg-slate-800/40 border border-slate-700 text-slate-500 text-xs flex items-center justify-center gap-2 px-3"
      >
        <AlertCircle size={14} /> PayPal setup required — admin must configure keys in .env
      </div>
    );
  }

  return (
    <div data-testid="paypal-buttons" className="min-h-[48px]">
      <div className="text-center overline text-slate-500 mb-2">{label}</div>
      <PayPalScriptProvider
        options={{
          clientId: config.client_id,
          currency: "USD",
          intent: "capture",
        }}
      >
        <PayPalButtons
          disabled={disabled}
          style={{ layout: "horizontal", color: "gold", shape: "pill", label: "paypal", height: 44, tagline: false }}
          createOrder={async () => {
            try {
              const { data } = await api.post("/payments/paypal/create-order", { event_id: eventId, use_credit: true });
              return data.order_id;
            } catch (err) {
              console.error("[PayPal] create order failed", err);
              toast.error(err.response?.data?.detail || "PayPal order failed");
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              await api.post(`/payments/paypal/capture-order/${data.orderID}`);
              toast.success("Payment successful");
              navigate("/profile");
            } catch (err) {
              console.error("[PayPal] capture failed", err);
              toast.error(err.response?.data?.detail || "Payment capture failed");
            }
          }}
          onCancel={() => toast.message("PayPal payment cancelled")}
          onError={(err) => {
            console.error("[PayPal] onError", err);
            toast.error("PayPal error — try another method");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
};
